import type { Session } from "@mux/sdk/v2/client"
import type { GlobalStore } from "./types"

// Runtime-owned session metadata, additive to the generated upstream contract.
type RevisionedSession = Session & { revision?: number }

/** The server's count of changes to the chat's row; 0 for a record that carries none. */
export function sessionRevision(session: Session) {
  return (session as RevisionedSession).revision ?? 0
}

type Revisions = Pick<GlobalStore, "session" | "session_removed">

/**
 * Whether a chat record from the server may take the place of `held`, the
 * store's copy. Every path that delivers records (events, pages, the sidebar
 * projection, lookups, seeds) answers to this one rule, so a record that left
 * the server before a change cannot undo it: the higher revision is the newer
 * record, and an equal one reports the same change. A chat the store removed
 * returns only with a later revision.
 */
export function admitsSession(store: Revisions, held: Session | undefined, incoming: Session) {
  if (held) return sessionRevision(incoming) >= sessionRevision(held)
  const removed = store.session_removed[incoming.id]
  return removed === undefined || sessionRevision(incoming) > removed
}

/** Fetched records with each replaced by the store's copy where that is newer; removed chats stay out. */
export function newestSessions(store: Revisions, incoming: readonly Session[]): Session[] {
  const held = new Map(store.session.map((session) => [session.id, session]))
  const result: Session[] = []
  for (const session of incoming) {
    const current = held.get(session.id)
    if (admitsSession(store, current, session)) result.push(session)
    else if (current) result.push(current)
  }
  return result
}

/** The revision to remember for a chat leaving the store: the newest seen of it. */
export function removedRevision(store: Revisions, sessionID: string, ...records: Array<Session | undefined>) {
  return Math.max(
    store.session_removed[sessionID] ?? 0,
    ...records.map((record) => (record ? sessionRevision(record) : 0)),
  )
}
